<template>
  <div>
    <div class="boq-md">
          <!-- DETAIL: líneas (boq_details) de la carga elegida. Ocupa todo el ancho;
               la lista de cargas (master) se elige desde el modal. -->
          <section class="boq-md__detail">
            <!-- Pantalla inicial: resumen de cargas + acciones (componente reutilizable). -->
            <EmptyContent
              v-if="!selectedBoq"
              :loading="isLoadingHeaders"
              :icon="boqHeaders.length ? 'folder-open' : 'folder-plus'"
              :title="boqHeaders.length ? $t('boqs_count_exist', { count: boqHeaders.length }) : $t('no_boqs')"
              :subtitle="boqHeaders.length ? $t('boqs_exist_hint') : $t('no_boqs_hint')"
              :actions="emptyActions"
              @action="onEmptyAction"
            />

            <template v-else>
              <BOQDetailsHeader
                :boq="selectedBoq"
                :loading="detailLoading"
                :validated="boqValidated"
                :validating="isValidating"
                :blocking="detailBlocking"
                :validate-hint="validateHint"
                :validate-chip-label="validateChipLabel"
                :validated-tooltip="boqValidatedTooltip"
                :is-civil="isCivilBoq"
                @change-boq="openBoqListModal"
                @validate="setValidated"
                @update="startUpdate"
                @link="openTemplateModal"
                @work-order="openWorkOrderModal"
                @mark-no-measurement-link="markNoMeasurementLinkBulk"
                @import="openImportModal"
              />

              <BOQDetails
                ref="boqDetails"
                :project-id="projectId"
                :boq="selectedBoq"
                @stats="onDetailStats"
                @loading="detailLoading = $event"
              />
            </template>
          </section>
        </div>

    <!-- Modal: Importar BOQs. El contenido pesado (plantillas, historial, estado del
         import) se monta solo al abrir gracias al v-if → carga perezosa. -->
    <epc-modal-wrapper
      :active.sync="showImportModal"
      :title="$t('import_boqs')"
      :width="'92vw'"
      :can-cancel="['escape']"
      scroll-body
    >
      <!-- Aviso compacto de modo actualización (componente genérico epc-alert). -->
      <div v-if="updateMode" class="epc-alert epc-alert--warning">
        <div class="epc-alert__text">
          <b-icon pack="fas" icon="rotate" size="is-small" />
          <span>{{ $t('update') }}: <strong>{{ updatePresetDescription }}</strong></span>
        </div>
        <button type="button" class="epc-alert__btn" @click="cancelUpdate">
          <b-icon pack="fas" icon="plus" size="is-small" /> {{ $t('new') }}
        </button>
      </div>

      <div class="boq-import-modal epc-data-table">
        <import-entities-optimized
          v-if="showImportModal"
          :key="updateMode ? ('upd-' + updateBoqId) : 'new'"
          :can-overwrite="true"
          :project-id="projectId"
          entity-type="boq_detail"
          :with-history="true"
          :preset-description="updateMode ? updatePresetDescription : ''"
          :force-update="updateMode"
          :preset-type="updateMode ? updateType : ''"
          :preset-work-order-id="updateMode ? updateWorkOrderId : null"
          :extra-payload="updateMode ? { boq_id: updateBoqId } : {}"
          @success="_HandleImportSuccess"
        />
      </div>

      <template #footer>
        <button class="epc-modal__button" @click="showImportModal = false">{{ $t('close') }}</button>
      </template>
    </epc-modal-wrapper>

    <!-- Modal: lista de cargas BOQ (master). Al elegir una se carga su detalle y se cierra. -->
    <epc-modal-wrapper
      :active.sync="showBoqListModal"
      :title="$t('boqs_list')"
      :width="560"
      :can-cancel="['escape']"
      scroll-body
    >
      <!-- Filtro por tipo: recarga las cargas del proyecto según disciplina. -->
      <b-field :label="$t('type')" class="mb-2">
        <b-select v-model="typeFilter" size="is-small" expanded @input="onTypeFilterChange">
          <option value="">{{ $t('all_types') }}</option>
          <option value="mechanical">{{ $t('mechanical_installation') }}</option>
          <option value="electrical">{{ $t('electrical_installation') }}</option>
          <option value="civil">{{ $t('civil_installation') }}</option>
        </b-select>
      </b-field>

      <b-input
        v-model="search"
        :placeholder="$t('search')"
        icon-pack="fas"
        icon="magnifying-glass"
        size="is-small"
        class="boq-md__search"
        expanded
        clearable />

      <div class="boq-md__scroll boq-md__scroll--modal">
        <div v-if="isLoadingHeaders" class="boq-md__loading">
          <b-icon pack="fas" icon="spinner" custom-class="fa-spin" />
        </div>
        <template v-else>
          <button
            v-for="boq in filteredHeaders"
            :key="boq.id"
            type="button"
            class="boq-card"
            :class="{ 'boq-card--active': boq.id === selectedBoqId }"
            @click="selectBoq(boq)">
            <div class="boq-card__body">
              <div class="boq-card__name" :title="boq.name">{{ boq.name }}</div>
              <div class="boq-card__meta">
                <b-tag :type="tagType(boq.type)" size="is-small">{{ $t(typeShortKey(boq.type)) }}</b-tag>
                <span><b-icon pack="fas" icon="list-ul" size="is-small" /> {{ boq.details_count }}</span>
                <span><b-icon pack="fas" icon="coins" size="is-small" /> {{ formatMoney(boq.details_sum_price_total) }}</span>
              </div>
            </div>
            <b-icon pack="fas" icon="chevron-right" size="is-small" class="boq-card__chev" />
          </button>
          <div v-if="!filteredHeaders.length" class="boq-md__empty">{{ $t('no_results') }}</div>
        </template>
      </div>
    </epc-modal-wrapper>

    <!-- Modal: reasociar la orden de pedido de la carga BOQ (civil). El chrome sale
         del wrapper compartido (estilos de epc-modal.scss); acá solo el contenido. -->
    <epc-modal-wrapper :active.sync="showWorkOrderModal" :title="$t('work_order')" :width="520">
      <b-field :label="$t('work_order')">
        <multiselect
          v-model="selectedModalWorkOrder"
          :options="workOrders"
          :custom-label="workOrderLabel"
          track-by="id"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          :placeholder="$t('select_work_order')"
          :loading="isLoadingWorkOrders">
          <template slot="singleLabel" slot-scope="{ option }">
            <span class="wo-opt">
              <span class="wo-opt__text">{{ option.code }}<template v-if="option.description"> · {{ option.description }}</template></span>
              <b-tag v-if="option.partners && option.partners.length" class="wo-opt__tag" rounded size="is-small">{{ option.partners[0].name }}</b-tag>
            </span>
          </template>
          <template slot="option" slot-scope="{ option }">
            <span class="wo-opt">
              <span class="wo-opt__text">{{ option.code }}<template v-if="option.description"> · {{ option.description }}</template></span>
              <b-tag v-if="option.partners && option.partners.length" class="wo-opt__tag" rounded size="is-small">{{ option.partners[0].name }}</b-tag>
            </span>
          </template>
          <template slot="noResult">{{ $t('no_elements_found') }}</template>
          <template slot="noOptions">{{ $t('no_options_available') }}</template>
        </multiselect>
      </b-field>

      <template #footer>
        <button class="epc-modal__button" @click="showWorkOrderModal = false">{{ $t('cancel') }}</button>
        <button class="epc-modal__button epc-modal__button--primary"
                :class="{ 'epc-modal__button--disabled': isSavingWorkOrder }"
                :disabled="isSavingWorkOrder" @click="saveWorkOrder">{{ $t('save') }}</button>
      </template>
    </epc-modal-wrapper>

    <!-- Modal: plantilla del BOQ = pestaña "Vinculación WBS - Presupuesto". -->
    <epc-modal-wrapper :active.sync="showTemplateModal" :title="$t('wbs_budget_link')" :width="1200" :can-cancel="['escape']" scroll-body>
      <boq-link-manager
        v-if="showTemplateModal"
        :project-id="projectId"
        :boqs="selectedBoq ? [{ ...selectedBoq, items: allDetails }] : []" />
      <template #footer>
        <button class="epc-modal__button" @click="showTemplateModal = false">{{ $t('close') }}</button>
      </template>

    </epc-modal-wrapper>

    <!-- Modal: confirmar marcar en masa "sin mediciones" (solo líneas sin asignaciones). -->
    <epc-modal-wrapper :active.sync="showMarkNoLinkModal" :title="$t('mark_no_measurement_link_bulk')" :width="440">
      <p style="font-size: 13px; color: rgb(30,48,70); line-height: 1.45">
        {{ $t('mark_no_measurement_link_bulk_confirm') }}
      </p>
      <template #footer>
        <button class="epc-modal__button" :disabled="isMarkingNoLink" @click="showMarkNoLinkModal = false">
          {{ $t('cancel') }}
        </button>
        <button
          class="epc-modal__button epc-modal__button--primary"
          :class="{ 'epc-modal__button--disabled': isMarkingNoLink }"
          :disabled="isMarkingNoLink"
          @click="doMarkNoMeasurementLinkBulk"
        >
          {{ isMarkingNoLink ? $t('saving') : $t('accept') }}
        </button>
      </template>
    </epc-modal-wrapper>
  </div>
</template>

<script>
  import axios from "axios";
  import Multiselect from 'vue-multiselect'
  import ImportEntitiesOptimized from "../import-entities-optimized.vue";
  import EpcModalWrapper from "../epc-modal-wrapper.vue";
  import BoqLinkManager from "./boq-link-manager.vue";
  import BOQDetails from "./components/BOQDetails.vue";
  import BOQDetailsHeader from "./components/BOQDetailsHeader.vue";
  import EmptyContent from "./components/EmptyContent.vue";

  export default {
    name: "ManageBoqs",

    components: {
      ImportEntitiesOptimized,
      Multiselect,
      EpcModalWrapper,
      BoqLinkManager,
      BOQDetails,
      BOQDetailsHeader,
      EmptyContent,
    },

    props: {
      projectId: { type: Number, required: true },
    },

    data() {
      return {
        showImportModal: false,
        showBoqListModal: false,
        typeFilter: "",
        search: "",
        boqHeaders: [],
        selectedBoqId: null,
        // Stats emitidos por <BOQDetails> (para el chip de validación en la cabecera).
        detailObserved: 0,    // líneas vinculadas que descuadran (déficit/excedente)
        detailUnassigned: 0,  // líneas sin ninguna medición vinculada
        detailLoading: false, // el detalle (BOQDetails) está cargando
        isValidating: false,
        // Todas las líneas (sin paginar) para el modal de vinculación WBS.
        allDetails: [],
        isLoadingHeaders: false,
        // Modo actualización (import sobre una carga existente).
        updateMode: false,
        updateBoqId: null,
        updatePresetDescription: "",
        updateType: "",          // tipo (legacy) fijo para el update
        updateWorkOrderId: null, // orden de pedido fija (civil)
        // Modal reasociar orden de pedido (civil).
        showWorkOrderModal: false,
        showTemplateModal: false,
        // Modal de confirmación "marcar sin mediciones (masivo)".
        showMarkNoLinkModal: false,
        isMarkingNoLink: false,
        workOrders: [],
        isLoadingWorkOrders: false,
        modalWorkOrderId: null,
        isSavingWorkOrder: false,
      };
    },

    computed: {
      // Filtra las cargas por nombre/code (buscador de la lista master).
      filteredHeaders() {
        const q = this.search.trim().toLowerCase();
        if (!q) return this.boqHeaders;
        return this.boqHeaders.filter(b =>
          (b.name || '').toLowerCase().includes(q) ||
          (b.code || '').toLowerCase().includes(q)
        );
      },
      selectedBoq() {
        const boq = this.boqHeaders.find(b => b.id === this.selectedBoqId) || null;
        return boq;
      },
      // ¿La carga seleccionada es civil? (BoqType 1) → habilita reasociar orden.
      isCivilBoq() {
        return this.selectedBoq && Number(this.selectedBoq.type) === 1;
      },
      // Proxy objeto↔id para el multiselect del modal.
      selectedModalWorkOrder: {
        get() {
          return this.workOrders.find(o => Number(o.id) === Number(this.modalWorkOrderId)) || null;
        },
        set(value) {
          this.modalWorkOrderId = value ? value.id : null;
        },
      },
      // Acciones de la pantalla inicial (con/sin cargas) para <EmptyContent>.
      emptyActions() {
        if (this.boqHeaders.length) {
          return [
            { name: 'manage', label: this.$t('manage_boqs'), icon: 'folder-open', type: 'is-primary' },
            { name: 'import', label: this.$t('import_new_boq'), icon: 'file-import', type: 'is-light' },
          ];
        }
        return [
          { name: 'import', label: this.$t('import_new_boq'), icon: 'file-import', type: 'is-primary' },
        ];
      },
      // ¿La carga seleccionada está validada?
      boqValidated() {
        return !!(this.selectedBoq && this.selectedBoq.validated_at);
      },
      // Total de líneas que impiden validar (observadas + sin asignar).
      detailBlocking() {
        return this.detailObserved + this.detailUnassigned;
      },
      // Etiqueta corta del chip según el caso predominante.
      validateChipLabel() {
        if (this.detailObserved > 0) return this.$t('boq_has_observations');
        return this.$t('boq_pending_assignments');
      },
      // Mensaje del tooltip adaptado a los casos que realmente existen.
      validateHint() {
        const obs = this.detailObserved > 0;
        const un = this.detailUnassigned > 0;
        if (obs && un) return this.$t('boq_validate_has_observations_hint');
        if (obs) return this.$t('boq_validate_observed_hint');
        return this.$t('boq_validate_unassigned_hint');
      },
      // Tooltip del estado validado: "por {usuario} · {fecha}".
      boqValidatedTooltip() {
        if (!this.boqValidated) return '';
        const by = this.selectedBoq.validated_by || '—';
        let when = '';
        try { when = new Date(this.selectedBoq.validated_at).toLocaleString('es-ES'); } catch (e) { when = ''; }
        return `${this.$t('validated')} · ${by}${when ? ' · ' + when : ''}`;
      },
    },

    mounted() {
      this.loadHeaders();
    },

    watch: {
      // Al cerrar el modal de vinculación, recarga los detalles (refresca los badges de
      // conteo) preservando las filas que estaban abiertas.
      showTemplateModal(open, wasOpen) {
        if (wasOpen && !open && this.selectedBoqId && this.$refs.boqDetails) {
          this.$refs.boqDetails.reload();
        }
      },
    },

    methods: {
      // MASTER: cargas de la tabla boqs (por tipo). Auto-selecciona la primera.
      // Carga las cabeceras (cargas BOQ). NO auto-selecciona: se muestra la pantalla
      // inicial (section) y el usuario elige "Gestionar BOQs" o importa una nueva.
      async loadHeaders() {
        this.isLoadingHeaders = true;
        this.selectedBoqId = null;
        try {
          const { data } = await axios.get(
            `/api/v2/project/${this.projectId}/boqs`,
            { params: { type: this.typeFilter || undefined } }
          );
          this.boqHeaders = (data && data.success && data.boqs) ? data.boqs : [];
        } catch (e) {
          this.$notify.error(this.$t('error_loading_documents'));
        } finally {
          this.isLoadingHeaders = false;
        }
      },

      // Acción emitida por <EmptyContent> (pantalla inicial).
      onEmptyAction(name) {
        if (name === 'manage') this.openBoqListModal();
        else if (name === 'import') this.openImportModal();
      },

      // Abre el modal con la lista de cargas (master).
      openBoqListModal() {
        this.showBoqListModal = true;
      },

      // El filtro por tipo recarga las cabeceras (afecta a la lista del modal).
      onTypeFilterChange() {
        this.loadHeaders();
      },

      // DETAIL: selecciona la carga; <BOQDetails> observa `boq` y carga su detalle. Cierra el modal.
      selectBoq(boq) {
        this.selectedBoqId = boq.id;
        this.showBoqListModal = false;    // al elegir, cierra el modal de cargas
      },

      // Stats emitidos por <BOQDetails> tras cargar el detalle (para el chip de validación).
      onDetailStats(stats) {
        this.detailObserved = (stats && stats.observed) || 0;
        this.detailUnassigned = (stats && stats.unassigned) || 0;
      },

      // --- Validación de la carga BOQ ---
      // Valida (flag=true) o anula (flag=false). El backend rechaza validar si hay observaciones.
      // Marca en masa como "sin mediciones": abre el modal de confirmación (EpcModalWrapper).
      markNoMeasurementLinkBulk() {
        if (!this.selectedBoq) return;
        this.showMarkNoLinkModal = true;
      },
      // Confirma y aplica el marcado masivo (solo líneas sin asignaciones).
      async doMarkNoMeasurementLinkBulk() {
        if (!this.selectedBoq || this.isMarkingNoLink) return;
        this.isMarkingNoLink = true;
        try {
          const { data } = await axios.patch(
            `/api/v2/project/${this.projectId}/boqs/${this.selectedBoqId}/mark-no-measurement-link`
          );
          if (data && data.success) {
            this.$notify.success('mark_no_measurement_link_bulk_done', { count: data.affected });
            this.showMarkNoLinkModal = false;
            if (this.$refs.boqDetails) this.$refs.boqDetails.reload();
          } else {
            this.$notify.error((data && data.message) || 'error');
          }
        } catch (e) {
          const msg = e && e.response && e.response.data && e.response.data.message;
          this.$notify.error(msg || 'error');
        } finally {
          this.isMarkingNoLink = false;
        }
      },
      setValidated(flag) {
        if (!this.selectedBoq) return;
        if (!flag) {
          this.$dialog.confirm({
            title: this.$t('boq_unvalidate'),
            message: this.$t('boq_unvalidate_confirm'),
            confirmText: this.$t('boq_unvalidate'),
            cancelText: this.$t('cancel'),
            type: 'is-warning',
            hasIcon: true,
            onConfirm: () => this.doSetValidated(false),
          });
          return;
        }
        this.doSetValidated(true);
      },
      async doSetValidated(flag) {
        this.isValidating = true;
        try {
          const { data } = await axios.patch(
            `/api/v2/project/${this.projectId}/boqs/${this.selectedBoqId}/validate`,
            { validated: flag }
          );
          if (data && data.success) {
            // Refleja el estado en la cabecera cacheada (selectedBoq es un find sobre boqHeaders).
            const row = this.boqHeaders.find((b) => b.id === this.selectedBoqId);
            if (row) {
              this.$set(row, 'validated_at', data.validated_at || null);
              this.$set(row, 'validated_by', data.validated_by || null);
            }
            this.$notify.success(flag ? 'boq_validated_ok' : 'boq_unvalidated_ok');
          } else {
            this.$notify.error((data && data.message) || 'error');
          }
        } catch (e) {
          const resp = e && e.response && e.response.data;
          if (resp && resp.message === 'boq_has_observations') {
            // Refresca los conteos (observadas / sin asignar) vía el detalle y avisa según el caso.
            if (this.$refs.boqDetails) await this.$refs.boqDetails.reload();
            this.$notify.error(this.validateHint);
          } else {
            this.$notify.error((resp && resp.message) || 'error');
          }
        } finally {
          this.isValidating = false;
        }
      },

      // Actualizar una carga: lleva al importador con descripción fija
      // (<name> -YYYYMMDD), update activado y boq_id de la carga.
      startUpdate() {
        if (!this.selectedBoq) return;
        const d = new Date();
        const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
        this.updateBoqId = this.selectedBoq.id;
        this.updatePresetDescription = `${this.selectedBoq.name} -${ymd}`;
        // Tipo (legacy) fijo + orden de pedido fija (civil) para bloquearlos en el import.
        this.updateType = this.typeLegacy(this.selectedBoq.type);
        this.updateWorkOrderId = this.selectedBoq.work_order ? this.selectedBoq.work_order.id : null;
        this.updateMode = true;
        this.showImportModal = true;
      },

      // Abrir el modal de importación en modo "carga nueva".
      openImportModal() {
        this.cancelUpdate();
        this.showImportModal = true;
      },

      // int del enum BoqType → string legacy que usa el select del import.
      // Numeración unificada: 1=civil, 2=mecánico, 3=eléctrico.
      typeLegacy(type) {
        const n = Number(type);
        if (n === 1) return 'civil';
        if (n === 2) return 'mechanical';
        return 'electrical';
      },

      // --- Plantilla del BOQ (componente de prueba en modal) ---
      async openTemplateModal() {
        if (!this.selectedBoq) return;
        this.allDetails = [];             // se rellena con la carga completa a continuación
        this.showTemplateModal = true;
        // El árbol del manager necesita TODAS las líneas: se cargan sin paginar bajo demanda.
        try {
          const { data } = await axios.get(
            `/api/v2/project/${this.projectId}/boq-details`,
            { params: { boq_id: this.selectedBoqId } }
          );
          if (data && data.success) this.allDetails = data.boqs || [];
        } catch (e) {
          // Si falla, el manager usa la página ya cargada.
        }
      },

      // --- Reasociar orden de pedido (solo civil) ---
      async openWorkOrderModal() {
        if (!this.selectedBoq) return;
        this.modalWorkOrderId = this.selectedBoq.work_order ? this.selectedBoq.work_order.id : null;
        this.showWorkOrderModal = true;
        this.isLoadingWorkOrders = true;
        try {
          const { data } = await axios.get('/api/v2/work-orders', { params: { project_id: this.projectId, with_partners: 1 } });
          this.workOrders = (data && data.success && data.data) ? data.data : [];
        } catch (e) {
          this.workOrders = [];
        } finally {
          this.isLoadingWorkOrders = false;
        }
      },
      workOrderLabel(o) {
        if (!o) return '';
        const base = o.description ? `${o.code} · ${o.description}` : `${o.code}`;
        const partner = (o.partners && o.partners.length) ? o.partners[0].name : null;
        return partner ? `${base} — ${partner}` : base;
      },
      async saveWorkOrder() {
        if (!this.selectedBoq || !this.modalWorkOrderId) {
          this.$notify.error(this.$t('select_work_order'));
          return;
        }
        this.isSavingWorkOrder = true;
        try {
          const { data } = await axios.patch(
            `/api/v2/project/${this.projectId}/boqs/${this.selectedBoq.id}/work-order`,
            { work_order_id: this.modalWorkOrderId }
          );
          if (data && data.success) {
            this.$notify.success('success_editing');
            this.showWorkOrderModal = false;
            const currentId = this.selectedBoqId;
            await this.loadHeaders();
            const found = this.boqHeaders.find(b => b.id === currentId);
            if (found) this.selectBoq(found);
          } else {
            this.$notify.error('error_saving');
          }
        } catch (e) {
          this.$notify.error('error_saving');
        } finally {
          this.isSavingWorkOrder = false;
        }
      },

      // Volver a "carga nueva" (sale del modo actualización).
      cancelUpdate() {
        this.updateMode = false;
        this.updateBoqId = null;
        this.updatePresetDescription = "";
      },

      // Tras importar/actualizar: recarga cabeceras y muestra por defecto la carga afectada.
      async _HandleImportSuccess() {
        const prevIds = new Set(this.boqHeaders.map((b) => b.id));
        const wasUpdate = this.updateMode;
        const updatedId = this.updateBoqId;

        this.cancelUpdate();
        this.showImportModal = false;
        await this.loadHeaders();

        let target = null;
        if (wasUpdate && updatedId) {
          // Actualización: la misma carga.
          target = this.boqHeaders.find((b) => b.id === updatedId) || null;
        } else {
          // Nueva import: la carga cuyo id no existía antes; si hay varias, la más nueva.
          const created = this.boqHeaders.filter((b) => !prevIds.has(b.id));
          target = created.length
            ? created.reduce((a, b) => (a.id > b.id ? a : b))
            : null;
        }
        // Fallback: la de mayor id (la más reciente) si no se identificó ninguna.
        if (!target && this.boqHeaders.length) {
          target = this.boqHeaders.reduce((a, b) => (a.id > b.id ? a : b));
        }
        if (target) this.selectBoq(target);
      },

      // --- helpers de presentación ---
      // El API serializa el enum BoqType como su valor. Numeración unificada:
      // 1=civil, 2=mecánico, 3=eléctrico.
      typeShortKey(type) {
        const n = Number(type);
        if (n === 1) return 'civil';
        if (n === 2) return 'mechanical';
        return 'electrical';
      },
      tagType(type) {
        const n = Number(type);
        if (n === 1) return 'is-success';   // civil
        if (n === 2) return 'is-info';      // mecánico
        return 'is-warning';                 // eléctrico
      },
      formatMoney(v) {
        const n = parseFloat(v);
        if (!Number.isFinite(n)) return '—';
        // Formato español: miles con punto, decimales con coma.
        return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      // Cantidades: formato español, hasta 4 decimales (sin ceros forzados).
      formatQty(v) {
        const n = parseFloat(v);
        if (!Number.isFinite(n)) return '—';
        return n.toLocaleString('es-ES', { maximumFractionDigits: 4 });
      },
    },
  };
</script>

<style scoped>
/* La tabla del detalle ocupa todo el ancho; la lista de cargas vive en un modal. */
.boq-md { display: block; }

/* La cabecera del detalle vive ahora en BOQDetailsHeader.vue. */

/* MASTER — lista de cargas (dentro del modal). */
.boq-md__search { margin-bottom: 8px; }
.boq-md__scroll {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
}
/* En el modal deja scrollear al cuerpo del modal (evita doble scroll). */
.boq-md__scroll--modal { max-height: none; overflow: visible; }
.boq-card {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}
.boq-card:hover {
  border-color: #7d99d4;
  box-shadow: 0 2px 8px rgba(51, 81, 143, .08);
}
.boq-card--active {
  border-color: #385da5;
  background: #f2f6ff;
  box-shadow: 0 2px 10px rgba(51, 81, 143, .12);
}
.boq-card__body { flex: 1; min-width: 0; }
.boq-card__name {
  font-weight: 600;
  color: #33518f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.boq-card__meta {
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: #7d8aa5;
  margin-top: 4px;
}
.boq-card__meta span { display: inline-flex; align-items: center; gap: 4px; }
.boq-card__chev { color: #c3ccdd; flex: 0 0 auto; }
.boq-md__loading, .boq-md__empty {
  padding: 24px;
  text-align: center;
  color: #9aa7c0;
}

/* DETAIL — líneas de la carga */
.boq-md__detail { flex: 1; min-width: 0; }

/* El detalle (tabla, footer, split y su scroll/sticky) vive ahora en BOQDetails.vue. */
/* Pantalla inicial: usa las clases globales .epc-empty* (epc-empty-state.scss). */
/* Cabecera del detalle movida a BOQDetailsHeader.vue. */

/* Estilos del detalle (foot/tabla/vinculadas/split) movidos a BOQDetails.vue. */

/* El aviso de actualización usa las clases globales .epc-alert* (epc-alert.scss). */

/* Compactado del import SOLO dentro de este modal (no toca el componente compartido). */
.boq-import-modal >>> .field:not(:last-child) { margin-bottom: 0.5rem; }
.boq-import-modal >>> .label { margin-bottom: 0.15rem; font-size: 0.8rem; }
.boq-import-modal >>> .input,
.boq-import-modal >>> .select select { font-size: 0.8rem; }
.boq-import-modal >>> .title { font-size: 1rem; margin-bottom: 0.5rem; }
.boq-import-modal >>> .subtitle { font-size: 0.85rem; margin-bottom: 0.5rem; }
.boq-import-modal >>> .box { padding: 0.75rem; }

/* Botones compactos. */
.boq-import-modal >>> .button {
  font-size: 0.8rem;
  height: 2rem;
  padding: 0 0.7rem;
}

/* Selects (Buefy) más bajos. */
.boq-import-modal >>> .select,
.boq-import-modal >>> .select select { height: 2rem; }
.boq-import-modal >>> .select:not(.is-multiple):not(.is-loading)::after { top: 55%; }

/* Multiselect (vue-multiselect) compacto. */
.boq-import-modal >>> .multiselect,
.boq-import-modal >>> .multiselect__tags { min-height: 2rem; font-size: 0.8rem; }
.boq-import-modal >>> .multiselect__tags { padding: 4px 40px 0 8px; }
.boq-import-modal >>> .multiselect__single,
.boq-import-modal >>> .multiselect__placeholder,
.boq-import-modal >>> .multiselect__input { font-size: 0.8rem; margin-bottom: 4px; padding-top: 2px; min-height: 18px; }
.boq-import-modal >>> .multiselect__select { height: 2rem; }

/* La tabla reutiliza el look global .epc-data-table (cabecera azul + filas compactas).
   Aquí solo neutralizamos el marco de card, ya que el wrapper no es una tarjeta. */
.boq-import-modal.epc-data-table {
  border: none;
  border-radius: 0;
  overflow: visible;
  background: transparent;
}
</style>
